/**
 * /app — Overview + Links combined view.
 *
 * Was a thin "stats stub + first 5 links" overview that duplicated a
 * standalone /app/links page. Folded together so the customer has one
 * place to see and manage every link, with at-a-glance metadata:
 *
 *   • Total visits in the last 7 days
 *   • Poof Shield on/off per link (badge)
 *   • Variations configured per link (badge)
 *   • Default action (lander vs redirect) inline
 *
 * Per-link click counts come from a single AE round-trip
 * (`/api/links/click-counts`) so we don't fan out N analytics calls
 * for a customer with 30 links. The list loads first; click counts
 * fill in second (typically ~100–200ms behind) — feels fast.
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Activity,
  ArrowRight,
  Copy,
  GitBranch,
  Layers,
  Link2,
  Plus,
  Shield,
  Sparkles,
} from 'lucide-react';
import {
  ApiError,
  analytics as analyticsApi,
  links as linksApi,
  type AnalyticsSummary,
  type Link,
} from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Dialog } from '@/components/Dialog';
import { InlineEditableTitle } from '@/components/InlineEditableTitle';
import { LiveNowCard } from '@/components/LiveNowCard';
import { MetricCard } from '@/components/MetricCard';
import { TodayVsYesterdayCard } from '@/components/TodayVsYesterdayCard';
import { WeekVsLastWeekCard } from '@/components/WeekVsLastWeekCard';
import {
  Badge,
  Button,
  ButtonLink,
  DataRow,
  EmptyState,
  StatCard,
} from '@/components/ui';

export default function Dashboard() {
  const [list, setList] = useState<Link[] | null>(null);
  const [clickCounts, setClickCounts] = useState<Record<string, number>>({});
  /** /api/analytics — drives the Live Now / Today vs Yesterday / Week vs
   *  Last Week stat cards above the link list. Separate from the
   *  click-counts call so the link rows paint fast even before the
   *  analytics round-trip lands. */
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [q, setQ] = useState('');
  /** When non-null, the duplicate-link dialog is open for that source link. */
  const [duplicateSource, setDuplicateSource] = useState<Link | null>(null);

  useEffect(() => {
    linksApi.list().then((r) => setList(r.links)).catch(() => setList([]));
    linksApi
      .clickCounts(7)
      .then((r) => setClickCounts(r.counts))
      .catch(() => {});
    analyticsApi.summary('7d').then(setAnalytics).catch(() => {});
    // Live Now / Today vs Yesterday read as stale fast — poll on the
    // same 30s cadence the analytics page uses (matches KV TTL too).
    const t = setInterval(() => {
      analyticsApi.summary('7d').then(setAnalytics).catch(() => {});
    }, 30_000);
    return () => clearInterval(t);
  }, []);

  /**
   * Optimistic-ish refresh: after a duplicate succeeds, re-fetch the
   * list so the new row appears with full metadata (badges, etc.).
   * Refreshing click counts isn't necessary — the new link starts at 0.
   */
  function refreshList() {
    linksApi.list().then((r) => setList(r.links)).catch(() => {});
  }

  const filtered = useMemo(() => {
    if (!list) return null;
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(
      (l) =>
        l.shortCode.toLowerCase().includes(needle) ||
        (l.title || '').toLowerCase().includes(needle) ||
        l.targetUrl.toLowerCase().includes(needle),
    );
  }, [list, q]);

  // (Previous Visits·7d card with sparkline + delta lived here; replaced
  // by the three first-class stat cards above the link list, fed from
  // /api/analytics. clickCounts is still used downstream for the
  // per-row visit badges; the daily series is no longer consumed.)

  const titleAction = list && list.length > 0 ? (
    <ButtonLink to="/app/links/new" variant="primary" size="sm" leadingIcon={<Plus size={16} />}>
      New link
    </ButtonLink>
  ) : null;

  if (list === null) {
    return (
      <DashboardLayout title="Overview">
        <div className="space-y-3">
          <div className="h-28 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
          <div className="h-20 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
          <div className="h-20 animate-pulse rounded-2xl bg-[var(--surface-glass)]" />
        </div>
      </DashboardLayout>
    );
  }

  if (list.length === 0) {
    return (
      <DashboardLayout title="Overview">
        <EmptyState
          icon={<Sparkles size={36} className="text-[var(--color-primary)]" />}
          title="Let's create your first link"
          description={
            <>
              Takes 10 seconds. Get a short{' '}
              <code className="mx-1 rounded-md bg-[var(--color-surface-container)] px-1.5 py-0.5 font-mono text-xs">
                go.poof.bio/&hellip;
              </code>{' '}
              link, drop it in your Instagram bio. When followers tap it, they
              escape Instagram&apos;s in-app browser straight to Safari.
            </>
          }
          cta={
            <ButtonLink to="/app/links/new" variant="primary" size="lg">
              Create my first link
            </ButtonLink>
          }
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Overview" titleAction={titleAction}>
      {/* Top stats — three first-class boxes for the most-watched signals:
          Live Now (visitors right now), Today vs Yesterday, This Week vs
          Last Week. Replaces the prior Visits/Links pair: the Visits card
          conflated week-vs-week with totals, and Links count was low-
          signal (visible scrolling through the list anyway). */}
      <div className="mb-7 grid gap-4 sm:grid-cols-3">
        {analytics ? (
          <LiveNowCard liveNow={analytics.liveNow} />
        ) : (
          <MetricCard label="Live now" value="—" meta="Loading…" />
        )}
        {analytics ? (
          <TodayVsYesterdayCard delta={analytics.delta} />
        ) : (
          <MetricCard label="Today vs yesterday" value="—" meta="Loading…" />
        )}
        {analytics ? (
          <WeekVsLastWeekCard weekDelta={analytics.weekDelta} />
        ) : (
          <MetricCard label="This week vs last" value="—" meta="Loading…" />
        )}
      </div>

      {/* Toolbar — section heading + search. CTA is in the title bar. */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="font-display text-xl font-bold tracking-tight">Your links</h2>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          className="ml-auto w-full max-w-xs rounded-full border border-[var(--color-border)] bg-[var(--surface-deep)] px-4 py-2 text-sm outline-none transition focus:border-[var(--color-ring)] sm:w-64"
        />
      </div>

      {filtered && filtered.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-on-surface-variant)]">
          No links match &ldquo;{q}&rdquo;.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {filtered!.map((l) => (
            <LinkRow
              key={l.id}
              link={l}
              clicks7d={clickCounts[`${hostFromFullUrl(l.fullUrl)}|/${l.shortCode}`]}
              onDuplicate={() => setDuplicateSource(l)}
              onUpdated={refreshList}
            />
          ))}
        </ul>
      )}

      {duplicateSource && (
        <DuplicateLinkDialog
          source={duplicateSource}
          onClose={() => setDuplicateSource(null)}
          onDuplicated={() => {
            setDuplicateSource(null);
            refreshList();
          }}
        />
      )}
    </DashboardLayout>
  );
}

// ---------------------------------------------------------------------------
// Per-link row — title + URL + meta badges + clicks + duplicate action.
// ---------------------------------------------------------------------------

function LinkRow({
  link,
  clicks7d,
  onDuplicate,
  onUpdated,
}: {
  link: Link;
  clicks7d: number | undefined;
  onDuplicate: () => void;
  /** Called after an inline title rename so the parent refetches the list. */
  onUpdated: () => void;
}) {
  const isPage = link.defaultAction === 'page';
  const variationCount =
    (link.variationCounts?.conditional ?? 0) + (link.variationCounts?.weighted ?? 0);
  const isPlaceholderTarget = link.targetUrl === 'https://poof.bio';

  return (
    <DataRow
      to={`/app/links/${link.id}`}
      trailing={
        <button
          type="button"
          onClick={onDuplicate}
          title="Duplicate this link with a new short code"
          className="flex items-center gap-1.5 px-4 text-xs font-medium text-[var(--color-on-surface-variant)] transition hover:bg-[var(--surface-glass)] hover:text-[var(--color-foreground)]"
        >
          <Copy size={13} />
          <span className="hidden sm:inline">Duplicate</span>
        </button>
      }
    >
      <DataRow.Stack>
        <DataRow.Title>
          <InlineEditableTitle
            value={link.title}
            fallback={link.shortCode}
            iconSize={13}
            onSave={async (next) => {
              await linksApi.update(link.id, { title: next });
              onUpdated();
            }}
          />
          {isPage ? (
            <Badge tone="tertiary">
              <Layers size={10} /> Lander
            </Badge>
          ) : (
            <Badge tone="secondary" title="Sends visitors straight to the destination URL">
              <ArrowRight size={10} /> Direct
            </Badge>
          )}
          {link.shieldEnabled && (
            <Badge tone="primary" title="Poof Shield is filtering bots + Meta crawlers">
              <Shield size={10} /> Shield
            </Badge>
          )}
          {link.ageGateEnabled && (
            <Badge tone="error" title="Visitors must confirm 18+ before this link resolves">
              18+
            </Badge>
          )}
          {variationCount > 0 && (
            <Badge tone="secondary" title="Variations override the link default per visitor">
              <GitBranch size={10} /> {variationCount}
            </Badge>
          )}
          {link.status !== 'active' && <Badge tone="neutral">{link.status}</Badge>}
        </DataRow.Title>
        <DataRow.Meta>
          {link.fullUrl}
          {isPage ? (
            <span> · serves landing page</span>
          ) : isPlaceholderTarget ? (
            <span> · destination not set</span>
          ) : (
            <span> → {link.targetUrl}</span>
          )}
        </DataRow.Meta>
      </DataRow.Stack>

      {/* Visits badge — right-aligned. Empty placeholder while click
          counts are still loading so the row doesn't jump. */}
      <div className="shrink-0 text-right">
        <div className="font-display text-xl font-bold tabular-nums text-[var(--color-foreground)]">
          {clicks7d !== undefined ? clicks7d.toLocaleString() : '—'}
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-on-surface-variant)]">
          visits · 7d
        </div>
      </div>
    </DataRow>
  );
}

function hostFromFullUrl(fullUrl: string): string {
  try {
    return new URL(fullUrl).hostname;
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Duplicate Link dialog — modal asking only for the new short code (and
// optionally a title). Everything else — target URL, attached page,
// Poof Shield config, variations — is copied verbatim server-side, so
// the customer can spin up 5 short codes of the same setup in seconds.
// ---------------------------------------------------------------------------

function DuplicateLinkDialog({
  source,
  onClose,
  onDuplicated,
}: {
  source: Link;
  onClose: () => void;
  onDuplicated: (newLink: Link) => void;
}) {
  const [shortCode, setShortCode] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const newLink = await linksApi.duplicate(source.id, {
        shortCode: shortCode.trim() || undefined,
        title: title.trim() || undefined,
      });
      onDuplicated(newLink);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'short_code_taken') {
          setError('That short code is already taken on this domain.');
        } else if (e.code === 'short_code_invalid') {
          setError('Short code must be 3–32 characters: letters, numbers, or hyphens.');
        } else {
          setError(`Couldn’t duplicate: ${e.code}`);
        }
      } else {
        setError('Something went wrong. Try again?');
      }
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={() => !busy && onClose()} dismissible={!busy}>
      <form onSubmit={submit}>
        <h2 className="font-display text-xl font-bold tracking-tight">Duplicate link</h2>
        <p className="mt-1.5 text-sm text-[var(--color-on-surface-variant)]">
          Copies everything from{' '}
          <code className="rounded-md bg-[var(--color-surface-container)] px-1.5 py-0.5 font-mono text-xs">
            /{source.shortCode}
          </code>{' '}
          — landing page, Poof Shield, variations, destination URL. Pick a
          new short code for the copy.
        </p>

        <label className="mt-5 block">
          <span className="block text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
            New short code
          </span>
          <input
            type="text"
            value={shortCode}
            onChange={(e) => setShortCode(e.target.value)}
            disabled={busy}
            autoFocus
            placeholder="Leave blank for a random one"
            className="mt-1.5 w-full rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-4 py-2.5 font-mono text-sm outline-none transition focus:border-[var(--color-ring)] disabled:opacity-50"
          />
          <span className="mt-1.5 block text-[11px] text-[var(--color-on-surface-variant)]">
            Letters, numbers, hyphens. 3–32 characters.
          </span>
        </label>

        <label className="mt-4 block">
          <span className="block text-xs font-semibold uppercase tracking-[0.06em] text-[var(--color-on-surface-variant)]">
            Title <span className="opacity-60">(optional)</span>
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
            placeholder={
              source.title ? `${source.title} (copy)` : 'Internal name for this copy'
            }
            className="mt-1.5 w-full rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-4 py-2.5 text-sm outline-none transition focus:border-[var(--color-ring)] disabled:opacity-50"
          />
          <span className="mt-1.5 block text-[11px] text-[var(--color-on-surface-variant)]">
            Internal name — only you see this. Visitors never do.
          </span>
        </label>

        {error && (
          <div className="mt-4 rounded-xl border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]">
            {error}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Duplicating…' : 'Duplicate'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
